import { Grid, TableCell, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ProformaEmployeeCostReportSectionRef } from "../../../../models/proforma-employee-cost-report/proforma-employee-cost-report-section-ref";
import ContributingSections from "./ContributingSections";
import { ProformaEmployeeCostReportContext } from "../ProformaEmployeeCostReport";
import { TextFieldTypeAmount } from "../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import { Container, StyledConfigHintContainer } from "./styles";
import EmployeeCostSectionAmountHint from "./EmployeeCostSectionAmountCellHint";
import { LegislationSocialSecurityCostSectionRef } from "../../../../models/legislation-social-security-cost/legislation-social-security-cost-section-ref";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp, faLock } from "@fortawesome/free-solid-svg-icons";
interface Props {
  currentSection: ProformaEmployeeCostReportSectionRef;
  handleUpdateSection: (updatedSection: ProformaEmployeeCostReportSectionRef) => void;
  contributingSections: ProformaEmployeeCostReportSectionRef[];
  proportionalTo: ProformaEmployeeCostReportSectionRef | null;
  socialSecuritySection: LegislationSocialSecurityCostSectionRef | null;
}

interface State {
  contributingToConfigChanged: boolean;
  proportionalToConfigChanged: boolean;
}
const EmployeeCostSectionAmountCell = ({
  currentSection,
  handleUpdateSection,
  contributingSections,
  proportionalTo,
  socialSecuritySection,
}: Props) => {
  const theme = useTheme();

  const { originalReport, viewMode, reportUpdated, setReportUpdated } = useContext(ProformaEmployeeCostReportContext);
  const originalSection = originalReport?.sections.find((section) => section.type?.id === currentSection.type?.id);

  const [state, setState] = useState<State>({
    contributingToConfigChanged: false,
    proportionalToConfigChanged: false,
  });

  //#region fixed amount
  const fixedAmount = useMemo(
    () =>
      currentSection.type && currentSection.type.fixedAmount > 0
        ? currentSection.type?.fixedAmount
        : socialSecuritySection &&
          !currentSection.type?.considerIncomeBrackets &&
          socialSecuritySection?.fixedAmount > 0
        ? socialSecuritySection?.fixedAmount
        : 0,
    [currentSection.type, socialSecuritySection]
  );
  //#endregion

  //#region min amount
  const minAmount = useMemo(
    () =>
      currentSection.type && currentSection.type.minAmount > 0
        ? currentSection.type?.minAmount
        : socialSecuritySection && !currentSection.type?.considerIncomeBrackets && socialSecuritySection.minAmount > 0
        ? socialSecuritySection.minAmount
        : 0,
    [currentSection.type, socialSecuritySection]
  );
  //#endregion

  //#region cap amount
  const capAmount = useMemo(
    () =>
      currentSection.type && currentSection.type.capAmount > 0
        ? currentSection.type?.capAmount
        : socialSecuritySection && !currentSection.type?.considerIncomeBrackets && socialSecuritySection.capAmount > 0
        ? socialSecuritySection?.capAmount
        : 0,
    [currentSection.type, socialSecuritySection]
  );
  //#endregion

  //#region divided by
  const dividedBy = useMemo(
    () =>
      currentSection.type && currentSection.type.dividedBy > 0
        ? currentSection.type?.dividedBy
        : socialSecuritySection?.dividedBy && socialSecuritySection?.dividedBy > 0
        ? socialSecuritySection?.dividedBy
        : 1,
    [currentSection.type, socialSecuritySection?.dividedBy]
  );
  //#endregion

  //#region multiplied by
  const multipliedBy = useMemo(
    () =>
      currentSection.type && currentSection.type.multipliedBy > 0
        ? currentSection.type?.multipliedBy
        : socialSecuritySection?.multipliedBy && socialSecuritySection?.multipliedBy > 0
        ? socialSecuritySection?.multipliedBy
        : 1,
    [currentSection.type, socialSecuritySection?.multipliedBy]
  );
  //#endregion

  //#region recalculate amount when contributing sections are updated
  const updateAmountUponCSChange = useCallback(() => {
    if (contributingSections.length > 0) {
      const amountsAdded: number[] = contributingSections
        .filter((currentSection) => !currentSection.type?.isSubstracted)
        .map((currentSection) => currentSection.amount);

      const totalAmountsAdded = amountsAdded.length > 0 ? amountsAdded.reduce((a: number, b: number) => a + b) : 0;

      const amountsSubstracted = contributingSections
        .filter((currentSection) => currentSection.type?.isSubstracted)
        .map((currentSection) => currentSection.amount);

      const totalAmountsSubstracted = amountsSubstracted.length > 0 ? amountsSubstracted.reduce((a, b) => a + b) : 0;
      setState((state) => ({
        ...state,
        contributingToConfigChanged:
          !reportUpdated &&
          originalSection?.amount !== ((totalAmountsAdded - totalAmountsSubstracted) * multipliedBy) / dividedBy,
      }));
      if (
        currentSection.amount !== ((totalAmountsAdded - totalAmountsSubstracted) * multipliedBy) / dividedBy &&
        !viewMode
      ) {
        handleUpdateSection({
          ...currentSection,
          amount: ((totalAmountsAdded - totalAmountsSubstracted) * multipliedBy) / dividedBy,
        });
      }
    }
  }, [
    contributingSections,
    currentSection,
    dividedBy,
    handleUpdateSection,
    multipliedBy,
    originalSection?.amount,
    reportUpdated,
    viewMode,
  ]);

  useEffect(() => {
    if (contributingSections.length > 0) {
      updateAmountUponCSChange();
    }
  }, [contributingSections]);
  //#endregion

  //#region recalculate when proportional to sections are updated (Non social security)
  const updateAmountUponPTSChange = useCallback(() => {
    if (
      proportionalTo &&
      (currentSection.manualRate || currentSection.systemRate) &&
      !currentSection.type?.autoCalculatedRate &&
      !viewMode
    ) {
      let newAmount: number | null = currentSection.manualRate
        ? (proportionalTo.amount / 100) * currentSection.manualRate
        : currentSection.systemRate
        ? (proportionalTo.amount / 100) * currentSection.systemRate
        : null;

      let withinIntervalAmount =
        capAmount > 0 && newAmount && newAmount > 0 && newAmount > capAmount
          ? (capAmount / dividedBy) * multipliedBy
          : minAmount > 0 && newAmount && newAmount > 0 && newAmount < minAmount
          ? (minAmount / dividedBy) * multipliedBy
          : newAmount && newAmount > 0
          ? (newAmount / dividedBy) * multipliedBy
          : newAmount;

      if (withinIntervalAmount && currentSection.amount !== withinIntervalAmount) {
        handleUpdateSection({
          ...currentSection,
          amount: withinIntervalAmount,
        });
        setState((state) => ({
          ...state,
          proportionalToConfigChanged: !reportUpdated && originalSection?.amount !== withinIntervalAmount,
        }));
      }
    }
  }, [
    proportionalTo,
    currentSection,
    handleUpdateSection,
    capAmount,
    dividedBy,
    minAmount,
    multipliedBy,
    originalSection?.amount,
    reportUpdated,
    viewMode,
  ]);

  useEffect(() => {
    if (
      proportionalTo &&
      (!currentSection.type?.type?.isSocialSecurityRelevant || !currentSection.type?.considerIncomeBrackets) //to avoid processing many times , the social security section amounts are calculated at EmployeeCostSection level
    ) {
      updateAmountUponPTSChange();
    }
  }, [
    proportionalTo,
    currentSection.manualRate,
    currentSection.systemRate,
    currentSection.type?.type?.isSocialSecurityRelevant,
    currentSection.type?.considerIncomeBrackets,
  ]);
  //#endregion

  return (
    <TableCell align="right">
      <Container>
        {!reportUpdated &&
        originalSection?.amount &&
        (state.contributingToConfigChanged || state.proportionalToConfigChanged) ? (
          <EmployeeCostSectionAmountHint
            previousAmount={originalSection.amount}
            contributesToConfigUpdated={state.contributingToConfigChanged}
            proportionalToConfigUpdated={state.proportionalToConfigChanged}
          />
        ) : (
          ""
        )}

        {currentSection.type?.type?.isSocialSecurityRelevant ||
        contributingSections.length > 0 ||
        (proportionalTo && !currentSection.type?.autoCalculatedRate) ? (
          //in case this section amount depends on another either by contributions or rate => we do not allow to modify
          contributingSections.length > 0 ? (
            <ContributingSections
              sectionName={currentSection.type?.name ?? ""}
              calculatedAmount={currentSection.amount}
              contributions={contributingSections}
              multipliedBy={currentSection.type?.multipliedBy}
              dividedBy={currentSection.type?.dividedBy}
            />
          ) : (
            <Grid
              style={{
                display: "flex",
                color: theme.palette.secondary.light,
                alignItems: "center",
              }}
            >
              <Typography align="right">{currentSection.amount.toLocaleCustom()}</Typography>
              <StyledConfigHintContainer>
                {capAmount > 0 && (
                  <Tooltip title={`Cap amount = ${capAmount.toLocaleCustom()}`}>
                    <span>
                      <FontAwesomeIcon icon={faCaretUp} />
                    </span>
                  </Tooltip>
                )}
                {minAmount > 0 && (
                  <Tooltip title={`Minimum amount = ${minAmount.toLocaleCustom()}`}>
                    <span>
                      <FontAwesomeIcon icon={faCaretDown} />
                    </span>
                  </Tooltip>
                )}
                {fixedAmount > 0 && (
                  <Tooltip title={`Fixed amount = ${fixedAmount.toLocaleCustom()}`}>
                    <span>
                      <FontAwesomeIcon icon={faLock} />
                    </span>
                  </Tooltip>
                )}
              </StyledConfigHintContainer>
            </Grid>
          )
        ) : (
          <Grid style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <Grid style={{ display: "flex" }}>
              <TextFieldTypeAmount
                field="amount"
                labelKey=""
                amountValue={currentSection.amount}
                handleChange={(_, number) => {
                  setReportUpdated(true);
                  handleUpdateSection({
                    ...currentSection,
                    amount: number ?? 0,
                  });
                }}
                disabled={viewMode}
                sxProps={{
                  width: "150px",
                  "& .MuiCircularProgress-root": {
                    display: "none",
                  },
                  "& .MuiInputBase-root": {
                    paddingRight: "0px",
                    borderRadius: "8px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px",
                    textAlign: "end",
                  },
                }}
                error={
                  (minAmount > 0 && currentSection.amount < minAmount) ||
                  (capAmount > 0 && currentSection.amount > capAmount)
                }
              />
              <StyledConfigHintContainer style={{ color: theme.palette.secondary.light }}>
                {capAmount > 0 && (
                  <Tooltip title={`Cap amount = ${capAmount.toLocaleCustom()}`}>
                    <span>
                      <FontAwesomeIcon icon={faCaretUp} />
                    </span>
                  </Tooltip>
                )}
                {minAmount > 0 && (
                  <Tooltip title={`Minimum amount = ${minAmount.toLocaleCustom()}`}>
                    <span>
                      <FontAwesomeIcon icon={faCaretDown} />
                    </span>
                  </Tooltip>
                )}
              </StyledConfigHintContainer>
            </Grid>

            {minAmount > 0 && currentSection.amount < minAmount && (
              <Typography
                variant="body2"
                color={theme.palette.error.main}
                style={{ textAlign: "start" }}
              >
                {`The minimum amount for this section is ${minAmount.toLocaleCustom()}`}
              </Typography>
            )}
            {capAmount > 0 && currentSection.amount > capAmount && (
              <Typography
                variant="body2"
                color={theme.palette.error.main}
                style={{ textAlign: "start" }}
              >
                {`The cap amount for this section is ${capAmount.toLocaleCustom()}`}
              </Typography>
            )}
          </Grid>
        )}
      </Container>
    </TableCell>
  );
};

export default EmployeeCostSectionAmountCell;
